import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import SEO from '../components/SEO';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

import {
  ArrowRight,
  Buildings,
  ChartBar,
  CheckCircle,
  CircleNotch,
  Clock,
  CurrencyDollar,
  Lightning,
  Lock,
  Sparkle,
  Target,
  TrendUp,
  WarningCircle
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';
const API_URL = BACKEND_URL;

/**
 * Blueprint Preview Page
 * 
 * Displays a read-only preview of a generated blueprint draft.
 * Accessible without authentication via draft_token.
 * Gates full access behind signup.
 */
const BlueprintPreview = () => {
  const { draftToken } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  
  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  useEffect(() => {
    const fetchBlueprint = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_URL}/api/blueprint-draft/${draftToken}`);
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || 'Failed to load blueprint');
        }
        
        if (data.success && data.draft) {
          setBlueprint(data.draft);
        } else {
          throw new Error('Invalid blueprint data');
        }
      } catch (err) {
        console.error('Error fetching blueprint:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (draftToken) {
      fetchBlueprint();
    }
  }, [draftToken]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Handle signup redirect with draft token
  const handleSignup = () => {
    // Store draft token for claiming after signup
    sessionStorage.setItem('blueprint_draft_token', draftToken);
    localStorage.setItem('pending_blueprint_claim', draftToken);
    // Store redirect destination for post-verify flow
    localStorage.setItem('post_auth_redirect', '/blueprint/continue');
    navigate('/register?source=blueprint&draft=' + draftToken);
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-[#161B26] min-h-screen">
        <SEO title="Loading Blueprint... | Man & Machine" />
        <LandingHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <CircleNotch size={48} className="text-[#6366F1] animate-spin mx-auto mb-4" />
            <p className="text-[#A0AEC0]">Loading your blueprint...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-[#161B26] min-h-screen">
        <SEO title="Blueprint Not Found | Man & Machine" />
        <LandingHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md px-6">
            <WarningCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Blueprint Not Found</h1>
            <p className="text-[#A0AEC0] mb-6">{error}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
            >
              Generate New Blueprint
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const process = blueprint?.process || {};
  const opportunities = blueprint?.opportunities || [];
  const roiSummary = blueprint?.roi_summary || {};

  return (
    <div className="bg-[#161B26] min-h-screen">
      <SEO 
        title={`${process.name || 'AI Blueprint'} | Man & Machine`}
        description="Your personalized AI automation blueprint - see opportunities and estimated ROI"
      />
      <LandingHeader />

      {/* Hero Section with Summary */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#6366F1] rounded-full filter blur-[150px] opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#10B981] rounded-full filter blur-[120px] opacity-15"></div>
        </div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.3)] mb-6">
              <Sparkle size={14} className="text-[#10B981]" />
              <span className="text-sm text-[#10B981] font-medium">Your AI Blueprint is Ready</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {process.name || 'AI Automation Blueprint'}
            </h1>
            <p className="text-xl text-[#A0AEC0] max-w-2xl mx-auto mb-4">
              {process.description || blueprint?.prompt}
            </p>
            
            {/* Reinforcement statement */}
            <p className="text-base text-[#8a90a8] max-w-xl mx-auto">
              This blueprint is used to generate deployable AI agents tailored to your business process.
            </p>
          </motion.div>

          {/* Key Metrics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.1 }}
            className="grid sm:grid-cols-3 gap-6 mb-8"
          >
            {/* Annual Savings */}
            <div className="glass rounded-2xl p-6 border border-[rgba(16,185,129,0.2)] text-center">
              <div className="w-12 h-12 rounded-xl bg-[rgba(16,185,129,0.1)] flex items-center justify-center mx-auto mb-4">
                <CurrencyDollar size={24} className="text-[#10B981]" />
              </div>
              <p className="text-3xl font-bold text-[#10B981] mb-1">
                {formatCurrency(roiSummary.estimated_annual_savings)}
              </p>
              <p className="text-sm text-[#A0AEC0]">Est. Annual Savings</p>
            </div>

            {/* Hours Saved */}
            <div className="glass rounded-2xl p-6 border border-[rgba(99,102,241,0.2)] text-center">
              <div className="w-12 h-12 rounded-xl bg-[rgba(99,102,241,0.1)] flex items-center justify-center mx-auto mb-4">
                <Clock size={24} className="text-[#6366F1]" />
              </div>
              <p className="text-3xl font-bold text-[#6366F1] mb-1">
                {roiSummary.total_hours_saved_annually || 0}
              </p>
              <p className="text-sm text-[#A0AEC0]">Hours Saved/Year</p>
            </div>

            {/* Opportunities */}
            <div className="glass rounded-2xl p-6 border border-[rgba(236,72,153,0.2)] text-center">
              <div className="w-12 h-12 rounded-xl bg-[rgba(236,72,153,0.1)] flex items-center justify-center mx-auto mb-4">
                <Target size={24} className="text-[#EC4899]" />
              </div>
              <p className="text-3xl font-bold text-[#EC4899] mb-1">
                {roiSummary.opportunities_count || opportunities.length}
              </p>
              <p className="text-sm text-[#A0AEC0]">AI Opportunities</p>
            </div>
          </motion.div>

          {/* Note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ...springConfig, delay: 0.2 }}
            className="text-center text-sm text-[#718096] italic"
          >
            {roiSummary.note || "Estimates based on industry benchmarks. Actual savings may vary."}
          </motion.p>
        </div>
      </section>

      {/* Opportunities Section */}
      <section className="py-16 bg-[rgba(255,255,255,0.02)]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Identified <span className="gradient-text">Opportunities</span>
            </h2>
            <p className="text-[#A0AEC0]">
              AI-powered automation opportunities ranked by impact and feasibility
            </p>
          </motion.div>

          <div className="space-y-6">
            {opportunities.map((opp, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                className="glass rounded-2xl p-6 border border-[rgba(255,255,255,0.05)] hover:border-[rgba(99,102,241,0.3)] transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  {/* Main Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center text-white font-bold text-sm">
                        {idx + 1}
                      </span>
                      <h3 className="text-xl font-semibold text-white">{opp.title}</h3>
                    </div>
                    <p className="text-[#A0AEC0] mb-4">{opp.description}</p>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        opp.impact === 'high' ? 'bg-[rgba(16,185,129,0.1)] text-[#10B981]' :
                        opp.impact === 'medium' ? 'bg-[rgba(99,102,241,0.1)] text-[#6366F1]' :
                        'bg-[rgba(156,163,175,0.1)] text-[#9CA3AF]'
                      }`}>
                        {opp.impact?.charAt(0).toUpperCase() + opp.impact?.slice(1)} Impact
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        opp.complexity === 'low' ? 'bg-[rgba(16,185,129,0.1)] text-[#10B981]' :
                        opp.complexity === 'medium' ? 'bg-[rgba(245,158,11,0.1)] text-[#F59E0B]' :
                        'bg-[rgba(239,68,68,0.1)] text-[#EF4444]'
                      }`}>
                        {opp.complexity?.charAt(0).toUpperCase() + opp.complexity?.slice(1)} Complexity
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-[rgba(99,102,241,0.1)] text-[#6366F1]">
                        {opp.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  </div>

                  {/* ROI Preview - Blurred */}
                  <div className="relative lg:w-48 flex-shrink-0">
                    <div className="glass rounded-xl p-4 text-center filter blur-sm">
                      <p className="text-2xl font-bold text-[#10B981]">
                        {formatCurrency(opp.roi?.annual_savings || 0)}
                      </p>
                      <p className="text-xs text-[#718096]">Annual Savings</p>
                      <div className="mt-2 text-sm text-[#A0AEC0]">
                        {opp.hours_saved_monthly || 0}h/month saved
                      </div>
                    </div>
                    {/* Lock Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-[rgba(10,11,13,0.7)] rounded-xl">
                      <Lock size={20} className="text-[#6366F1]" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Unlock Full Blueprint */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-full filter blur-[200px] opacity-10"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <div className="glass rounded-2xl p-8 md:p-12 border border-[rgba(99,102,241,0.3)]">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center mx-auto mb-6">
                <Lock size={32} className="text-white" />
              </div>

              {/* Dynamic impact statement */}
              <p className="text-lg text-[#A0AEC0] mb-4 max-w-xl mx-auto">
                This blueprint identifies an estimated annual impact of{' '}
                <span className="text-[#10B981] font-semibold">
                  {formatCurrency(roiSummary.estimated_annual_savings)}
                </span>{' '}
                and outlines the AI agents required to achieve it.
              </p>

              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Unlock Full <span className="gradient-text">Deployment Blueprint</span>
              </h2>
              
              <p className="text-base text-[#8a90a8] mb-8 max-w-xl mx-auto">
                Create a free account to access detailed ROI breakdowns, implementation guides, 
                and turn these opportunities into deployable AI agents.
              </p>

              {/* What you get */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8 text-left max-w-md mx-auto">
                {[
                  "Detailed ROI calculations",
                  "Implementation roadmaps",
                  "AI agent build plans",
                  "Save & deploy your process"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-[#10B981] flex-shrink-0" />
                    <span className="text-[#E2E8F0] text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={handleSignup}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white text-lg font-semibold rounded-xl hover:opacity-90 transition-opacity"
                data-testid="unlock-blueprint-btn"
              >
                Unlock Full Deployment Blueprint & Agent Build Plan
                <ArrowRight size={20} />
              </button>

              <p className="text-sm text-[#718096] mt-4">
                No credit card required. Start in under 2 minutes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlueprintPreview;
